import type {
    CustomCheckpointNetwork,
    PerformanceDatapointType,
    GQLWaterfallContext,
    GQLWaterfallSettings,
} from 'owa-analytics-types';
import { DataSource } from 'owa-analytics-types';
import { trace } from 'owa-trace';
import { addTimingToWaterfall } from './addTimingToWaterfall';

export async function addTimingsToGQLQuery<GQLQueryReturnType>(
    perfDatapoint: PerformanceDatapointType | CustomCheckpointNetwork | null | undefined,
    settings: GQLWaterfallSettings | null | undefined = null,
    gqlCallback: (context: GQLWaterfallContext) => Promise<GQLQueryReturnType>
): Promise<GQLQueryReturnType> {
    if (!perfDatapoint || settings?.isEnabled === false) {
        return gqlCallback({});
    }

    const isPredefined = (perfDatapoint as PerformanceDatapointType)?.isPerformanceDatapoint;
    const customCheckpoint = perfDatapoint as CustomCheckpointNetwork;
    const dp = isPredefined
        ? (perfDatapoint as PerformanceDatapointType)
        : customCheckpoint.datapoint;

    if (!dp.hasEnded) {
        addTimingToWaterfall(
            dp,
            isPredefined,
            'Data_S',
            customCheckpoint?.indexes?.[0],
            customCheckpoint?.name,
            settings?.discardIfDefined
        );

        dp.dataSource = null;
    }

    const context = {
        perfDatapoint: dp,
        waterfallSettings: settings,
    };

    let response: GQLQueryReturnType;
    try {
        response = await gqlCallback(context);
    } catch (error) {
        if (settings?.recordEndOnError && !dp.hasEnded) {
            try {
                const endCheckpoint = isPredefined ? 'Data_E' : customCheckpoint.indexes[1];
                if (!dp.isWaterfallCheckpointDefined(endCheckpoint)) {
                    addTimingToWaterfall(
                        dp,
                        isPredefined,
                        'Data_E',
                        customCheckpoint?.indexes?.[1],
                        customCheckpoint?.name,
                        settings?.discardIfDefined
                    );
                }
            } catch {
                // Timing instrumentation must not replace the original query failure.
                trace.warn('Failed to record the GraphQL rejection waterfall checkpoint');
            }
        }
        throw error;
    }

    if (!dp.hasEnded) {
        if (dp.dataSource === null && !dp.didExecuteGqlQuery) {
            dp.dataSource = DataSource.NO_QUERY;
        }

        addTimingToWaterfall(
            dp,
            isPredefined,
            'Data_E',
            customCheckpoint?.indexes?.[1],
            customCheckpoint?.name,
            settings?.discardIfDefined
        );
    }

    return response;
}
